import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Receipt, ExternalLink, Download } from 'lucide-react';

/**
 * Stripe invoices table — fetched from /api/billing/invoices.
 */
export default function InvoicesTable({ invoices }) {
  if (!invoices || invoices.length === 0) return null;
  return (
    <Card data-testid="invoices-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-indigo-500" />
          <CardTitle className="text-lg">Invoices</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-2 text-gray-500 font-medium text-xs">Invoice</th>
                <th className="text-left py-2 px-2 text-gray-500 font-medium text-xs">Date</th>
                <th className="text-left py-2 px-2 text-gray-500 font-medium text-xs hidden sm:table-cell">Period</th>
                <th className="text-right py-2 px-2 text-gray-500 font-medium text-xs">Amount</th>
                <th className="text-center py-2 px-2 text-gray-500 font-medium text-xs">Status</th>
                <th className="text-right py-2 px-2 text-gray-500 font-medium text-xs">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {invoices.map((inv) => (
                <tr key={inv.invoice_id} data-testid={`invoice-${inv.invoice_id}`}>
                  <td className="py-2.5 px-2">
                    <span className="font-medium text-gray-800">{inv.number || inv.invoice_id.slice(-8)}</span>
                    {inv.description && <p className="text-xs text-gray-400 truncate max-w-[200px]">{inv.description}</p>}
                  </td>
                  <td className="py-2.5 px-2 text-gray-600">
                    {new Date(inv.created).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td className="py-2.5 px-2 text-gray-500 text-xs hidden sm:table-cell">
                    {inv.period_start && inv.period_end && (
                      <>{new Date(inv.period_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {new Date(inv.period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</>
                    )}
                  </td>
                  <td className="py-2.5 px-2 text-right font-semibold text-gray-800">
                    ${inv.amount_paid?.toFixed(2) || inv.amount_due?.toFixed(2)}
                  </td>
                  <td className="py-2.5 px-2 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                      inv.status === 'open' ? 'bg-blue-100 text-blue-700' :
                      inv.status === 'draft' ? 'bg-gray-100 text-gray-600' :
                      inv.status === 'void' ? 'bg-red-100 text-red-600' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {inv.status === 'paid' ? 'Paid' : inv.status?.charAt(0).toUpperCase() + inv.status?.slice(1)}
                    </span>
                  </td>
                  <td className="py-2.5 px-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {inv.hosted_invoice_url && (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                          className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors rounded hover:bg-blue-50"
                          title="View invoice"
                          data-testid={`invoice-view-${inv.invoice_id}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {inv.invoice_pdf && (
                        <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer"
                          className="p-1.5 text-gray-400 hover:text-teal-600 transition-colors rounded hover:bg-teal-50"
                          title="Download PDF"
                          data-testid={`invoice-pdf-${inv.invoice_id}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
